import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Insights() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();
  const { ref: chartsRef, isVisible: chartsVisible } = useScrollAnimation();

  // Real data from 33 facilities (21 hospitals + 12 diagnostic centers)
  
  // Hospital category distribution
  const hospitalCategoryData = [
    { category: 'Affordable', count: 5, percentage: 24 },
    { category: 'Mid-Range', count: 8, percentage: 38 },
    { category: 'Premium', count: 8, percentage: 38 }
  ];

  // Diagnostic center category distribution
  const diagnosticCategoryData = [
    { category: 'Affordable', count: 3, percentage: 25 },
    { category: 'Mid-Range', count: 3, percentage: 25 },
    { category: 'Premium', count: 6, percentage: 50 }
  ];

  // Hospital cost range analysis
  const hospitalCostData = [
    { range: '₹10K-50K', facilities: 5, label: 'Affordable' },
    { range: '₹50K-150K', facilities: 8, label: 'Mid-Range' },
    { range: '₹150K-350K', facilities: 8, label: 'Premium' }
  ];

  // Diagnostic cost range analysis
  const diagnosticCostData = [
    { range: '₹800-2K', facilities: 3, label: 'Affordable' },
    { range: '₹2K-3.5K', facilities: 3, label: 'Mid-Range' },
    { range: '₹3.5K-5.5K', facilities: 6, label: 'Premium' }
  ];

  // Facility type distribution
  const facilityTypeData = [
    { name: 'Hospitals', value: 21, color: '#14B8A6' },
    { name: 'Diagnostic Centers', value: 12, color: '#F59E0B' }
  ];

  // Wait time analysis (top 8 fastest)
  const waitTimeData = [
    { name: 'Redcliffe Labs', time: 15 },
    { name: 'NM Medical', time: 15 },
    { name: 'Nidan Diagnostic', time: 18 },
    { name: 'Parvati Diagnostic', time: 18 },
    { name: 'Gupte Hospital', time: 20 },
    { name: 'Apollo Diagnostics', time: 20 },
    { name: 'PH Diagnostic', time: 20 },
    { name: 'AIMS Hospital', time: 20 }
  ];

  // Location distribution
  const locationData = [
    { area: 'Central Pune', count: 8, percentage: 24 },
    { area: 'East Pune', count: 7, percentage: 21 },
    { area: 'West Pune', count: 6, percentage: 18 },
    { area: 'South Pune', count: 7, percentage: 21 },
    { area: 'Pimpri-Chinchwad', count: 5, percentage: 15 }
  ];

  // Top rated facilities
  const topRatedData = [
    { name: 'Deenanath Mangeshkar', rating: 4.9 },
    { name: 'Lifeberries Health', rating: 4.9 },
    { name: 'Aditya Birla Memorial', rating: 4.9 },
    { name: 'Ruby Hall Clinic', rating: 4.9 },
    { name: 'Jehangir Hospital', rating: 4.8 }
  ];

  const COLORS = ['#10B981', '#F59E0B', '#8B5CF6'];

  const scrollToHospitals = () => {
    document.getElementById('hospitals')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="insights" className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 ${headerVisible ? 'animate-fade-in-down' : 'opacity-0'}`}
        >
          <h2 className="text-5xl font-bold text-gray-900 mb-4">Healthcare Insights at a Glance</h2>
          <p className="text-xl text-gray-600">Real Data-Driven Analytics from 33 Facilities</p>
        </div>

        {/* Top Stats */}
        <div
          ref={statsRef}
          className={`grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 ${statsVisible ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'}`}
        >
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-hospital-line text-3xl text-teal-600"></i>
            </div>
            <div className="text-4xl font-bold text-teal-600 mb-2">21</div>
            <div className="text-gray-600 font-medium">Total Hospitals</div>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-test-tube-line text-3xl text-amber-600"></i>
            </div>
            <div className="text-4xl font-bold text-amber-600 mb-2">12</div>
            <div className="text-gray-600 font-medium">Diagnostic Centers</div>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-star-line text-3xl text-purple-600"></i>
            </div>
            <div className="text-4xl font-bold text-purple-600 mb-2">4.6</div>
            <div className="text-gray-600 font-medium">Average Rating</div>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-3xl text-blue-600"></i>
            </div>
            <div className="text-4xl font-bold text-blue-600 mb-2">27</div>
            <div className="text-gray-600 font-medium">Avg Wait Time (min)</div>
          </div>
        </div>

        {/* Charts Grid */}
        <div
          ref={chartsRef}
          className={`grid lg:grid-cols-2 gap-8 ${chartsVisible ? 'animate-fade-in-up animation-delay-400' : 'opacity-0'}`}
        >
          {/* Hospital Category Distribution - Bar Chart */}
          <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                <i className="ri-bar-chart-box-line text-xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Hospital Distribution</h3>
                <p className="text-xs text-gray-600">By affordability category</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hospitalCategoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="category" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(value: number) => [`${value} hospitals`, 'Count']}
                />
                <Bar dataKey="count" fill="#14B8A6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 flex justify-around text-center">
              {hospitalCategoryData.map((item, idx) => (
                <div key={idx}>
                  <div className="text-xl font-bold text-gray-900">{item.percentage}%</div>
                  <div className="text-xs text-gray-600">{item.category}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Diagnostic Center Distribution - Bar Chart */}
          <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <i className="ri-bar-chart-line text-xl text-amber-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Diagnostic Center Distribution</h3>
                <p className="text-xs text-gray-600">By affordability category</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={diagnosticCategoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="category" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(value: number) => [`${value} centers`, 'Count']}
                />
                <Bar dataKey="count" fill="#F59E0B" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 flex justify-around text-center">
              {diagnosticCategoryData.map((item, idx) => (
                <div key={idx}>
                  <div className="text-xl font-bold text-gray-900">{item.percentage}%</div>
                  <div className="text-xs text-gray-600">{item.category}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hospital Cost Analysis - Line Chart */}
          <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <i className="ri-line-chart-line text-xl text-emerald-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Hospital Cost Spectrum</h3>
                <p className="text-xs text-gray-600">Delivery cost ranges</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={hospitalCostData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="range" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(value: number) => [`${value} hospitals`, 'Count']}
                />
                <Line type="monotone" dataKey="facilities" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-3 text-center">
              <div className="text-xs text-gray-600">Cost Range: <span className="font-bold text-gray-900">₹10,000 - ₹3,50,000</span></div>
            </div>
          </div>

          {/* Diagnostic Cost Analysis - Line Chart */}
          <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-pulse-line text-xl text-blue-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Diagnostic Cost Spectrum</h3>
                <p className="text-xs text-gray-600">Test package ranges</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={diagnosticCostData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="range" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(value: number) => [`${value} centers`, 'Count']}
                />
                <Line type="monotone" dataKey="facilities" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-3 text-center">
              <div className="text-xs text-gray-600">Cost Range: <span className="font-bold text-gray-900">₹800 - ₹5,500</span></div>
            </div>
          </div>

          {/* Facility Type Distribution - Pie Chart */}
          <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-pie-chart-line text-xl text-purple-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Facility Type Split</h3>
                <p className="text-xs text-gray-600">Total 33 facilities</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={facilityTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  style={{ fontSize: '11px' }}
                >
                  {facilityTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-3 flex justify-around text-center">
              <div>
                <div className="text-xl font-bold text-teal-600">21</div>
                <div className="text-xs text-gray-600">Hospitals (64%)</div>
              </div>
              <div>
                <div className="text-xl font-bold text-amber-600">12</div>
                <div className="text-xs text-gray-600">Diagnostics (36%)</div>
              </div>
            </div>
          </div>

          {/* Wait Time Analysis - Horizontal Bar Chart */}
          <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                <i className="ri-timer-line text-xl text-rose-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Fastest Wait Times</h3>
                <p className="text-xs text-gray-600">Top 8 facilities (minutes)</p>
              </div>
            </div>
            <div className="space-y-2">
              {waitTimeData.map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-gray-700 truncate">{item.name}</span>
                    <span className="text-xs font-bold text-gray-900">{item.time} min</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-gradient-to-r from-teal-500 to-emerald-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${(item.time / 30) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Location Coverage */}
        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <i className="ri-map-pin-line text-xl text-indigo-600"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Location Coverage Across Pune</h3>
              <p className="text-xs text-gray-600">Facility distribution by area</p>
            </div>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {locationData.map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl font-bold text-indigo-600 mb-2">{item.count}</div>
                <div className="text-xs font-medium text-gray-900 mb-3">{item.area}</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full"
                    style={{ width: `${item.percentage * 4}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-600 mt-1">{item.percentage}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Rated Facilities */}
        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
              <i className="ri-star-fill text-xl text-yellow-600"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Top Rated Facilities</h3>
              <p className="text-xs text-gray-600">Highest patient satisfaction</p>
            </div>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {topRatedData.map((item, idx) => (
              <div key={idx} className="text-center p-4 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl">
                <div className="text-xl font-bold text-yellow-600 mb-2">{item.rating}</div>
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className={`ri-star-fill text-xs ${i < Math.floor(item.rating) ? 'text-yellow-500' : 'text-gray-300'}`}></i>
                  ))}
                </div>
                <div className="text-xs font-medium text-gray-700">{item.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Insights Summary */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl border-2 border-emerald-200">
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-check-line text-xl text-white"></i>
            </div>
            <h4 className="text-base font-bold text-gray-900 mb-2">Balanced Options</h4>
            <p className="text-sm text-gray-700">24% affordable hospitals ensure accessible care for all families across Pune.</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border-2 border-purple-200">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-vip-crown-line text-xl text-white"></i>
            </div>
            <h4 className="text-base font-bold text-gray-900 mb-2">Premium Care</h4>
            <p className="text-sm text-gray-700">38% premium hospitals offer advanced facilities and specialized maternal care.</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-map-2-line text-xl text-white"></i>
            </div>
            <h4 className="text-base font-bold text-gray-900 mb-2">Wide Coverage</h4>
            <p className="text-sm text-gray-700">33 facilities strategically located across 5 major areas of Pune for easy access.</p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <button 
            onClick={scrollToHospitals}
            className="px-8 py-4 bg-teal-600 text-white rounded-full font-semibold text-base hover:bg-teal-700 transition-all hover:shadow-lg shadow-teal-600/30 whitespace-nowrap inline-flex items-center gap-2"
          >
            <span>Explore All Hospitals</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
